/**
 * Order and colour of the grouped series of a portfolio chart (SPEC-0053 §4.3-4.4).
 *
 * A series style is `[{ valueId, rank, colorToken }]` for one (combined) Group by field. Rank 1 is
 * the bottom of the stack and the first entry of the legend and the tooltip. Colours are tokens of
 * the OFFOLIO categorical chart palette, never free hex values (VIS-01).
 */
import { NO_VALUE_ID, OTHERS_ID } from "./chartFieldCatalogue";

/** The 12 categorical chart colours (VIS-01). Overlays keep their reserved colours (SERIES_COLOR_MAP). */
export const CHART_PALETTE = [
  { token: "chart-cat-1", color: "#3b5ba5" },
  { token: "chart-cat-2", color: "#e07b39" },
  { token: "chart-cat-3", color: "#2a9d8f" },
  { token: "chart-cat-4", color: "#c44569" },
  { token: "chart-cat-5", color: "#8e6cc9" },
  { token: "chart-cat-6", color: "#d4a017" },
  { token: "chart-cat-7", color: "#4a90c2" },
  { token: "chart-cat-8", color: "#7a9a3a" },
  { token: "chart-cat-9", color: "#b5651d" },
  { token: "chart-cat-10", color: "#5d6d7e" },
  { token: "chart-cat-11", color: "#d35d8b" },
  { token: "chart-cat-12", color: "#3d8361" },
];
export const NO_VALUE_TOKEN = "chart-cat-none";
const NEUTRAL_COLOR = "#b8b5c2";

const COLOR_BY_TOKEN = Object.fromEntries(CHART_PALETTE.map((p) => [p.token, p.color]));
COLOR_BY_TOKEN[NO_VALUE_TOKEN] = NEUTRAL_COLOR;

export const PICKABLE_TOKENS = [...CHART_PALETTE.map((p) => p.token), NO_VALUE_TOKEN];
export const isColorToken = (token) => Object.prototype.hasOwnProperty.call(COLOR_BY_TOKEN, token);
export const tokenColor = (token) => COLOR_BY_TOKEN[token] || NEUTRAL_COLOR;

/** Groups shown at most; the rest is merged into "Others" (SPEC-0053 §7). */
export const MAX_SERIES = 30;

const isNoValue = (valueId) => String(valueId).split(" - ").every((p) => p === NO_VALUE_ID);

/** Default style: the given order, palette colours in turn, the grey token for "(No value)". */
export const defaultSeriesStyle = (orderedValueIds) => {
  let i = 0;
  return orderedValueIds.map((valueId, idx) => ({
    valueId,
    rank: idx + 1,
    colorToken: isNoValue(valueId) ? NO_VALUE_TOKEN : CHART_PALETTE[i++ % CHART_PALETTE.length].token,
  }));
};

/**
 * Full style for the values at hand: saved values first in their saved rank, then values never
 * saved in default order with the next free palette colours. Saved values no longer present are
 * dropped; ranks are renumbered 1..n. A value keeps its saved `hidden` flag (false when unsaved).
 * @param {string[]} defaultOrder every value id, already in default order
 * @param {{valueId: string, rank: number, colorToken: string, hidden?: boolean}[]} saved
 */
export const resolveSeriesStyle = (defaultOrder, saved) => {
  if (!saved || saved.length === 0) return defaultSeriesStyle(defaultOrder).map((s) => ({ ...s, hidden: false }));
  const present = new Set(defaultOrder);
  const savedByValue = new Map();
  saved.forEach((s) => {
    if (s && present.has(s.valueId) && !savedByValue.has(s.valueId)) savedByValue.set(s.valueId, s);
  });
  const savedOrdered = Array.from(savedByValue.values()).sort((a, b) => a.rank - b.rank);
  const used = new Set(savedOrdered.map((s) => s.colorToken).filter(isColorToken));
  const free = CHART_PALETTE.map((p) => p.token).filter((tk) => !used.has(tk));
  let next = 0;
  const nextColor = (valueId) => {
    if (isNoValue(valueId)) return NO_VALUE_TOKEN;
    if (free.length) return free[next++ % free.length];
    return CHART_PALETTE[next++ % CHART_PALETTE.length].token;
  };
  const result = savedOrdered.map((s) => ({
    valueId: s.valueId,
    colorToken: isColorToken(s.colorToken) ? s.colorToken : nextColor(s.valueId),
    hidden: Boolean(s.hidden),
  }));
  defaultOrder.forEach((v) => {
    if (!savedByValue.has(v)) result.push({ valueId: v, colorToken: nextColor(v), hidden: false });
  });
  return result.map((s, i) => ({ ...s, rank: i + 1 }));
};

/** Payload of the PUT: ranks renumbered in list order. */
export const toSavePayload = (style) =>
  style.map((s, i) => ({ valueId: s.valueId, rank: i + 1, colorToken: s.colorToken, hidden: Boolean(s.hidden) }));

/* ---- Edits (SPEC-0053 v2): each returns a new style, or the same one when nothing changes ---- */

export const moveSeries = (style, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= style.length || to >= style.length) return style;
  const items = style.slice();
  const [moved] = items.splice(from, 1);
  items.splice(to, 0, moved);
  return items.map((s, i) => ({ ...s, rank: i + 1 }));
};

export const setSeriesColor = (style, valueId, colorToken) => {
  if (!isColorToken(colorToken)) return style;
  const target = style.find((s) => s.valueId === valueId);
  if (!target || target.colorToken === colorToken) return style;
  return style.map((s) => (s.valueId === valueId ? { ...s, colorToken } : s));
};

export const toggleSeriesHidden = (style, valueId) =>
  style.some((s) => s.valueId === valueId)
    ? style.map((s) => (s.valueId === valueId ? { ...s, hidden: !s.hidden } : s))
    : style;

export const hiddenValueIds = (style) => new Set((style || []).filter((s) => s.hidden).map((s) => s.valueId));

export { OTHERS_ID };
